import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Grid, useMediaQuery, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import useSWR from 'swr';
import moment from 'moment';
import { AccountService, Currency, SquareService, StripeService } from '../api';
import { ApiError } from '../api/core/ApiError';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';
import { useApiResponseHandlerContext } from '../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../common/apiNotifications/ApiResponseHandlerContext';
import { getDateFormat } from '../common/getDateAndTimeFormat';
import { mb } from '../common/theme/mbTokens';
import { BookingFeeCard } from './BookingFeeCard';
import { DisconnectProviderDialog } from './DisconnectProviderDialog';
import { PaymentProvider, PROVIDER_NAMES, ProviderCard, ProviderCardRow, ProviderCardState } from './ProviderCard';

const STRIPE_AUTHORIZE_ENDPOINT_URL = 'https://connect.stripe.com/oauth/authorize';
const STRIPE_RETURN_PATH = '/dashboard/stripe';
const STRIPE_TERMINAL_URL = 'https://dashboard.stripe.com/terminal';
const STRIPE_TEST_TERMINAL_URL = 'https://dashboard.stripe.com/test/terminal';
const PAYMENTS_PATH = '/dashboard/payments';
const SQUARE_ACCESS_DENIED_ERROR = 'access_denied';
const SQUARE_NOT_USD_CODE = 'not_usd';
const SQUARE_CANNOT_PROCESS_CODE = 'cannot_process';

type ConnectError = { messageKey: string; currency?: string };

const getSquareConnectError = (error: unknown): ConnectError | null => {
  const detail = error instanceof ApiError ? error.body?.detail : undefined;
  if (detail?.code === SQUARE_NOT_USD_CODE) {
    return { messageKey: 'payments.error.notUsd', currency: detail.currency };
  }
  if (detail?.code === SQUARE_CANNOT_PROCESS_CODE) {
    return { messageKey: 'payments.error.cannotProcess' };
  }
  return null;
};

export const PaymentsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const safeExecute = useSafeExecute();
  const { showResponseNotification } = useApiResponseHandlerContext();
  const { account, updateLocalAccount } = useGetAccountAuthenticated({});
  const stripeTestModeEnabled = useFeatureFlagEnabled('stripe-test-mode-enabled');
  const hasExecutedRef = useRef(false);
  const queryParams = new URLSearchParams(location.search);
  const codeQueryParam = queryParams.get('code');
  const stateQueryParam = queryParams.get('state');
  const errorQueryParam = queryParams.get('error');
  const isStripeReturn = location.pathname === STRIPE_RETURN_PATH;
  const [connectingProvider, setConnectingProvider] = useState<PaymentProvider | null>(
    codeQueryParam ? (isStripeReturn ? 'stripe' : 'square') : null,
  );
  const [connectError, setConnectError] = useState<ConnectError | null>(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState<PaymentProvider | null>(null);

  const squareConnect = account.square_connect;
  const stripeConnect = account.stripe_connect?.stripe_account_id ? account.stripe_connect : undefined;
  const isSquareLost = !!squareConnect?.connection_lost_at;
  const { data: squareStatus } = useSWR(squareConnect ? 'squareStatus' : null, () => SquareService.getSquareStatus());

  const capabilities = account.payment_integration_settings?.capabilities;
  const { data: stripeReaders } = useSWR(
    stripeConnect && capabilities?.card_readers ? ['stripeReaders'] : null,
    () => StripeService.getStripeReaders(),
    { revalidateOnFocus: false },
  );

  let squareState: ProviderCardState = 'idle';
  if (squareConnect) {
    squareState = isSquareLost ? 'lost' : 'connected';
  } else if (stripeConnect) {
    squareState = 'locked';
  }
  let stripeState: ProviderCardState = 'idle';
  if (stripeConnect) {
    stripeState = 'connected';
  } else if (squareConnect) {
    stripeState = 'locked';
  }

  useEffect(() => {
    if (hasExecutedRef.current) {
      return;
    }
    if (errorQueryParam === SQUARE_ACCESS_DENIED_ERROR && !isStripeReturn) {
      hasExecutedRef.current = true;
      setConnectError({ messageKey: 'payments.error.denied' });
      navigate(PAYMENTS_PATH, { replace: true });
      return;
    }
    if (!codeQueryParam) {
      return;
    }
    hasExecutedRef.current = true;
    const finishConnect = async () => {
      try {
        if (isStripeReturn) {
          const isTestMode = stateQueryParam ? !!JSON.parse(decodeURIComponent(stateQueryParam))?.isTestMode : false;
          await updateLocalAccount(await AccountService.createStripeIntegration(codeQueryParam, isTestMode));
        } else {
          await updateLocalAccount(
            await SquareService.connectSquare({ code: codeQueryParam, state: stateQueryParam ?? '' }),
          );
        }
      } catch (error) {
        const squareConnectError = isStripeReturn ? null : getSquareConnectError(error);
        if (squareConnectError) {
          setConnectError(squareConnectError);
        } else {
          const detail = error instanceof ApiError ? error.body?.detail : undefined;
          showResponseNotification(
            typeof detail === 'string' ? detail : detail?.message ?? t('api.error.default'),
            ApiResponseHandlerType.ERROR,
          );
        }
      }
      setConnectingProvider(null);
      navigate(PAYMENTS_PATH, { replace: true });
    };
    void finishConnect();
  }, [codeQueryParam, errorQueryParam]);

  const getErrorMessage = (error: ApiError) =>
    typeof error.body?.detail === 'string' ? error.body.detail : t('api.error.default');

  const handleConnectSquare = async () => {
    setConnectError(null);
    setConnectingProvider('square');
    try {
      const { url } = await safeExecute({
        apiCall: () => SquareService.getSquareConnectUrl(),
        successMessage: '',
        suppressSuccessMessage: true,
        onErrorMessageGenerator: getErrorMessage,
      });
      window.location.href = url;
    } catch (error) {
      setConnectingProvider(null);
    }
  };

  const handleConnectStripe = () => {
    const authUrlParams = new URLSearchParams({
      response_type: 'code',
      client_id: stripeTestModeEnabled
        ? import.meta.env.VITE_REACT_APP_TEST_STRIPE_CLIENT_ID
        : import.meta.env.VITE_REACT_APP_STRIPE_CLIENT_ID,
      scope: 'read_write',
      redirect_uri: `${window.location.origin}${STRIPE_RETURN_PATH}`,
      state: JSON.stringify({ isTestMode: stripeTestModeEnabled }),
    }).toString();
    setConnectError(null);
    setConnectingProvider('stripe');
    window.location.href = `${STRIPE_AUTHORIZE_ENDPOINT_URL}?${authUrlParams}`;
  };

  const handleDisconnect = async (actionConfirmed: boolean) => {
    if (!actionConfirmed) {
      setDisconnectingProvider(null);
      return;
    }
    try {
      if (disconnectingProvider === 'square') {
        await updateLocalAccount(
          await safeExecute({
            apiCall: () => SquareService.disconnectSquare(),
            successMessage: '',
            suppressSuccessMessage: true,
            onErrorMessageGenerator: getErrorMessage,
          }),
        );
      } else {
        await safeExecute({
          apiCall: () => AccountService.disconnectStripeIntegration(),
          successMessage: '',
          suppressSuccessMessage: true,
          onErrorMessageGenerator: getErrorMessage,
        });
        await updateLocalAccount();
      }
      setDisconnectingProvider(null);
    } catch (error) {
      // safeExecute already showed the error; the dialog stays open
    }
  };

  const formatDate = (date?: string) => (date ? moment(date).format(getDateFormat(account).longDateFormat) : undefined);
  const toRows = (rows: { label: string; value?: string; isMonospace?: boolean }[]): ProviderCardRow[] =>
    rows.filter((row): row is ProviderCardRow => !!row.value);

  const squareRows = toRows(
    squareState === 'lost'
      ? [
          { label: t('payments.field.business'), value: squareConnect?.business_name },
          { label: t('payments.field.merchantId'), value: squareConnect?.merchant_id, isMonospace: true },
          {
            label: t('payments.field.renewalsWaiting'),
            value: squareStatus ? String(squareStatus.renewals_waiting_count ?? 0) : undefined,
          },
        ]
      : [
          { label: t('payments.field.business'), value: squareConnect?.business_name },
          { label: t('payments.field.location'), value: squareConnect?.location_name },
          { label: t('payments.field.merchantId'), value: squareConnect?.merchant_id, isMonospace: true },
          { label: t('payments.field.connectedOn'), value: formatDate(squareConnect?.connected_at) },
        ],
  );
  const stripeRows = toRows([
    { label: t('payments.field.stripeAccountName'), value: stripeConnect?.account_display_name },
    { label: t('payments.field.stripeAccountId'), value: stripeConnect?.stripe_account_id, isMonospace: true },
    { label: t('payments.field.connectedOn'), value: formatDate(stripeConnect?.created_at) },
  ]);
  if (stripeConnect && capabilities?.card_readers) {
    stripeRows.push({
      label: t('payments.field.cardReaders'),
      value: (stripeReaders ?? []).map((reader) => reader.label).join(', '),
      link: {
        label: t('payments.addReader'),
        href: stripeConnect.is_stripe_test_account ? STRIPE_TEST_TERMINAL_URL : STRIPE_TERMINAL_URL,
      },
    });
  }

  const squareLostActions = (
    <>
      <LoadingButton
        variant="contained"
        loading={connectingProvider === 'square'}
        onClick={handleConnectSquare}
        fullWidth={isPhone}>
        {t('payments.reconnect')}
      </LoadingButton>
      <Button variant="text" color="inherit" onClick={() => setDisconnectingProvider('square')} fullWidth={isPhone}>
        {t('payments.removeConnection')}
      </Button>
    </>
  );
  const isLostActionsSticky = isPhone && squareState === 'lost';

  const renderActions = (provider: PaymentProvider, state: ProviderCardState) => {
    const providerName = PROVIDER_NAMES[provider];
    if (state === 'idle' || state === 'locked') {
      return (
        <LoadingButton
          variant="outlined"
          loading={connectingProvider === provider}
          disabled={state === 'locked'}
          onClick={provider === 'square' ? handleConnectSquare : handleConnectStripe}>
          {t('payments.connect', { provider: providerName })}
        </LoadingButton>
      );
    }
    if (state === 'connected') {
      return (
        <Button variant="outlined" color="error" onClick={() => setDisconnectingProvider(provider)}>
          {t('payments.disconnect')}
        </Button>
      );
    }
    return isLostActionsSticky ? undefined : squareLostActions;
  };

  const isNothingConnected = !squareConnect && !stripeConnect;

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <Grid container flexDirection="column" gap={2.5} sx={{ p: mb.layout.cardPadding, maxWidth: 1040, flexShrink: 0 }}>
        <Grid container flexDirection="column" gap={0.5}>
          <Typography variant="h4" fontWeight={700}>
            {t('payments.title')}
          </Typography>
          <Typography color="text.secondary">{t('payments.lead')}</Typography>
        </Grid>
        {connectError && (
          <Alert severity="error" onClose={() => setConnectError(null)}>
            {t(connectError.messageKey, { currency: connectError.currency })}
          </Alert>
        )}
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <ProviderCard
              provider="square"
              state={squareState}
              isSandbox={squareConnect?.is_sandbox}
              lostDate={formatDate(squareConnect?.connection_lost_at)}
              rows={squareRows}
              actions={renderActions('square', squareState)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ProviderCard
              provider="stripe"
              state={stripeState}
              isSandbox={account.stripe_connect?.is_stripe_test_account}
              isTaxLocked={capabilities?.sales_tax_locked}
              rows={stripeRows}
              actions={renderActions('stripe', stripeState)}
            />
          </Grid>
        </Grid>
        {account.currency === Currency.USD && (
          <BookingFeeCard account={account} updateLocalAccount={updateLocalAccount} />
        )}
        {isNothingConnected && <Alert severity="info">{t('payments.onlyOne')}</Alert>}
      </Grid>
      {isLostActionsSticky && (
        <Grid
          container
          flexDirection="column"
          gap={1}
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 'auto',
            p: 1.5,
            bgcolor: mb.surface,
            borderTop: `1px solid ${mb.lineSoft}`,
            boxShadow: `0 -6px 8px -2px rgba(31, 26, 23, 0.06)`,
          }}>
          {squareLostActions}
        </Grid>
      )}
      <DisconnectProviderDialog
        provider={disconnectingProvider}
        renewalsWaitingCount={squareStatus?.renewals_waiting_count ?? 0}
        onClose={handleDisconnect}
      />
    </Grid>
  );
};
